import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { useSnackbar } from "notistack";
import { useSelector, useDispatch } from "react-redux";
import { setCurrentProfile } from "../../store/slices/profilesSlice";

const PurchaseModal = ({
  open,
  onClose,
  onPurchase,
  loading,
  selectedPackage,
  setLoading,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { profiles, currentProfile } = useSelector((state) => state.profiles);
  const dispatch = useDispatch();
  const [showProfileSelector, setShowProfileSelector] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    language: i18n.language,
    profileId: currentProfile?.id || "",
    notes: "",
  });

  const [errors, setErrors] = useState({});

  const availableLanguages = Object.keys(i18n.options.resources);

  useEffect(() => {
    // Only show profile selector if user has more than one profile
    if (profiles && profiles.length > 1) {
      setShowProfileSelector(true);
    } else if (profiles && profiles.length === 1) {
      // Auto-select the only profile
      setFormData((prev) => ({
        ...prev,
        profileId: profiles[0].id,
      }));
      setShowProfileSelector(false);
    }
  }, [profiles]);

  // When modal opens, use the global currentProfile unless a profile was already selected locally
  useEffect(() => {
    if (open) {
      setFormData((prev) => ({
        ...prev,
        profileId: currentProfile?.id || prev.profileId,
      }));
      setErrors({});
    }
  }, [open, currentProfile]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim())
      newErrors.firstName = t("validation.required", {
        field: t("purchaseModal.firstName"),
      });
    if (!formData.lastName.trim())
      newErrors.lastName = t("validation.required", {
        field: t("purchaseModal.lastName"),
      });
    if (!formData.email.trim()) {
      newErrors.email = t("validation.required", {
        field: t("purchaseModal.email"),
      });
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("validation.invalidEmail");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    try {
      if (!validateForm()) return;
      setLoading(true);
      const success = await onPurchase({
        packageCode: selectedPackage.packageCode,
        userData: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          language: formData.language,
        },
        profileId: formData.profileId || null,
        notes: formData.notes,
      });
      if (success) onClose();
    } catch (error) {
      const errorMessage = error.response?.data?.message
        ? t(`esim.purchase.error.${error.response.data.message}`)
        : t("esim.purchase.error.processingError");
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  if (!selectedPackage) return null;

  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t("purchaseModal.title")}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t("purchaseModal.packageDetails")}
          </Typography>
          <Typography variant="body2">
            {t("purchaseModal.name")}: {selectedPackage.name}
          </Typography>
          <Typography variant="body2">
            {t("purchaseModal.price")}:{" "}
            {(selectedPackage.price / 10000).toFixed(2)} {t("credits.balance")}
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("purchaseModal.firstName")}
              error={!!errors.firstName}
              helperText={errors.firstName}
              value={formData.firstName}
              onChange={(e) =>
                setFormData({ ...formData, firstName: e.target.value })
              }
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("purchaseModal.lastName")}
              error={!!errors.lastName}
              helperText={errors.lastName}
              value={formData.lastName}
              onChange={(e) =>
                setFormData({ ...formData, lastName: e.target.value })
              }
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("purchaseModal.email")}
              type="email"
              error={!!errors.email}
              helperText={errors.email}
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              disabled={loading}
            />
          </Grid>
          {showProfileSelector ? (
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>{t("purchaseModal.profile")}</InputLabel>
                <Select
                  value={formData.profileId}
                  onChange={(e) => {
                    setFormData({ ...formData, profileId: e.target.value });
                    dispatch(setCurrentProfile(e.target.value));
                  }}
                  label={t("purchaseModal.profile")}
                  disabled={loading}
                  error={!formData.profileId}
                >
                  {profiles.map((profile) => (
                    <MenuItem key={profile.id} value={profile.id}>
                      {profile.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {t("purchaseModal.selectProfileHelp")}
                </FormHelperText>
              </FormControl>
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>{t("purchaseModal.language")}</InputLabel>
              <Select
                value={formData.language}
                onChange={(e) =>
                  setFormData({ ...formData, language: e.target.value })
                }
                label={t("purchaseModal.language")}
                disabled={loading}
              >
                {availableLanguages.map((lang) => (
                  <MenuItem key={lang} value={lang}>
                    {t(`languages.${lang}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("purchaseModal.notes")}
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              disabled={loading}
              placeholder={t("purchaseModal.notesPlaceholder")}
              helperText={t("purchaseModal.notesHelp")}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t("common.actions.cancel")}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            t("common.actions.purchase")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchaseModal;
